<script>
/* global axios */
/*
 * Legacy report UI still uses PT bare strings in the template (unchanged from
 * pre-v647). Only bare-string rules are scoped here — new JS in this file must
 * stay free of nested-ternary / style disables.
 */
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { Line } from 'vue-chartjs';
import RevenueTrackingSettings from './RevenueTrackingSettings.vue';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const fmtNum = n => (n == null ? '0' : Number(n).toLocaleString('pt-BR'));
const fmtPct = n => `${Number(n || 0).toFixed(1)}%`;
const fmtMoney = (n, cur = 'BRL') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: cur }).format(
    n || 0
  );

const CATEGORY_LABELS = {
  marketing: 'Marketing',
  utility: 'Utilidade',
  authentication: 'Autenticação',
  service: 'Serviço',
};

const CATEGORY_ICONS = {
  marketing: 'i-ri-megaphone-line',
  utility: 'i-ri-wrench-line',
  authentication: 'i-ri-shield-check-line',
  service: 'i-ri-customer-service-line',
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = days => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

export default {
  name: 'WhatsappReports',

  // vue-chartjs export is named Line (HTML reserved); keep import name for the library API.
  // eslint-disable-next-line vue/no-reserved-component-names, vue/match-component-import-name
  components: { Line, RevenueTrackingSettings },

  props: {
    inboxId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      period: '7d',
      customFrom: daysAgoISO(6),
      customTo: todayISO(),
      loading: false,
      error: null,
      report: null,
      showRevenueSettings: false,
    };
  },

  computed: {
    accountId() {
      return this.$route.params.accountId;
    },

    dateRange() {
      if (this.period === '7d') return { from: daysAgoISO(6), to: todayISO() };
      if (this.period === '30d')
        return { from: daysAgoISO(29), to: todayISO() };
      return { from: this.customFrom, to: this.customTo };
    },

    // ── Report shortcuts ──
    totals() {
      return this.report?.totals || {};
    },

    cost() {
      return (
        this.report?.cost || {
          total: 0,
          currency: 'BRL',
          by_category: {},
          billable_counts: {},
        }
      );
    },

    previous() {
      return this.report?.comparison || {};
    },

    // Revenue only exists for accounts that turned sales tracking on, so the
    // whole block stays hidden until a sale is actually attributed.
    revenue() {
      return (
        this.report?.revenue || {
          total: 0,
          conversions_count: 0,
          avg_ticket: 0,
          currency: 'BRL',
          roi_percent: null,
          revenue_per_reply: 0,
        }
      );
    },

    hasRevenue() {
      return (this.revenue.conversions_count || 0) > 0;
    },

    roiLabel() {
      const roi = this.revenue.roi_percent;
      if (roi === null || roi === undefined) return '—';
      return `${roi >= 0 ? '+' : ''}${Number(roi).toFixed(0)}%`;
    },

    roiClass() {
      const roi = this.revenue.roi_percent;
      if (roi === null || roi === undefined) return 'text-n-slate-12';
      return roi >= 0
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';
    },

    topCampaigns() {
      return this.report?.top_campaigns || [];
    },

    // ── Heatmap ──
    // Returns rows in Seg..Dom order (Postgres DOW: 0=Sun..6=Sat).
    // Each row is { label, cells: [24 cells] }
    heatmapRows() {
      const raw = this.report?.hourly_heatmap || {};
      const order = [
        { dow: 1, label: 'Seg' },
        { dow: 2, label: 'Ter' },
        { dow: 3, label: 'Qua' },
        { dow: 4, label: 'Qui' },
        { dow: 5, label: 'Sex' },
        { dow: 6, label: 'Sáb' },
        { dow: 0, label: 'Dom' },
      ];
      return order.map(({ dow, label }) => {
        const dayData = raw[dow] || raw[String(dow)] || {};
        const cells = [];
        for (let h = 0; h < 24; h += 1) {
          const c = dayData[h] ||
            dayData[String(h)] || { sent: 0, read: 0, read_rate: 0 };
          cells.push({ hour: h, ...c });
        }
        return { label, cells };
      });
    },

    heatmapHasData() {
      return this.heatmapRows.some(row => row.cells.some(c => c.sent > 0));
    },

    // ── Trend deltas ──
    sentDelta() {
      return this.computeDelta(
        this.totals.total_sent,
        this.previous.total_sent
      );
    },

    deliveryDelta() {
      return this.computeDelta(
        this.totals.delivery_rate,
        this.previous.delivery_rate,
        true
      );
    },

    readDelta() {
      return this.computeDelta(
        this.totals.read_rate,
        this.previous.read_rate,
        true
      );
    },

    replyDelta() {
      return this.computeDelta(
        this.totals.reply_rate,
        this.previous.reply_rate,
        true
      );
    },

    // ── Alerts ──
    alertLevel() {
      const fr = this.totals.failure_rate || 0;
      if (fr >= 15) return 'red';
      if (fr >= 5) return 'amber';
      return null;
    },

    // ── Chart ──
    chartData() {
      const stats = this.report?.daily_stats || [];
      const labels = stats.map(s => {
        const [, m, d] = s.date.split('-');
        return `${d}/${m}`;
      });
      return {
        labels,
        datasets: [
          {
            label: 'Enviadas',
            data: stats.map(s => s.sent || 0),
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99,102,241,0.12)',
            tension: 0.35,
            fill: true,
            pointRadius: 3,
          },
          {
            label: 'Entregues',
            data: stats.map(s => s.delivered || 0),
            borderColor: '#14b8a6',
            tension: 0.35,
            fill: false,
            pointRadius: 3,
          },
          {
            label: 'Lidas',
            data: stats.map(s => s.read || 0),
            borderColor: '#f59e0b',
            tension: 0.35,
            fill: false,
            pointRadius: 3,
          },
        ],
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'top',
            labels: { boxWidth: 12, font: { size: 12 } },
          },
          tooltip: {
            callbacks: {
              label(ctx) {
                const val = ctx.parsed.y;
                return `${ctx.dataset.label}: ${val.toLocaleString('pt-BR')}`;
              },
            },
          },
        },
        scales: {
          x: { grid: { display: false } },
          y: { beginAtZero: true, ticks: { stepSize: 1 } },
        },
      };
    },
  },

  watch: {
    period() {
      if (this.period !== 'custom') this.fetchReport();
    },
    customFrom() {
      if (this.period === 'custom') this.fetchReport();
    },
    customTo() {
      if (this.period === 'custom') this.fetchReport();
    },
  },

  mounted() {
    this.fetchReport();
  },

  methods: {
    fmtNum,
    fmtPct,
    fmtMoney,

    categoryLabel(key) {
      return CATEGORY_LABELS[key] || key;
    },

    categoryIcon(key) {
      return CATEGORY_ICONS[key] || 'i-ri-circle-line';
    },

    computeDelta(current, previous, isPercent = false) {
      if (previous == null || previous === 0) {
        if (current == null || current === 0)
          return { value: 0, direction: 'flat', label: '—' };
        return { value: 100, direction: 'up', label: 'novo' };
      }
      if (isPercent) {
        const diff = (current || 0) - previous;
        return {
          value: Math.abs(diff),
          direction: this.deltaDirection(diff),
          label: `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}pp`,
        };
      }
      const pct = (((current || 0) - previous) / previous) * 100;
      return {
        value: Math.abs(pct),
        direction: this.deltaDirection(pct),
        label: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`,
      };
    },

    deltaDirection(value) {
      if (value > 0) return 'up';
      if (value < 0) return 'down';
      return 'flat';
    },

    deltaClass(delta, higherIsBetter = true) {
      if (delta.direction === 'flat') return 'text-n-slate-11';
      const up = delta.direction === 'up';
      const good = up === higherIsBetter;
      return good
        ? 'text-green-600 dark:text-green-400'
        : 'text-red-600 dark:text-red-400';
    },

    heatmapCellClass(cell) {
      if (!cell || cell.sent === 0) {
        return 'bg-n-slate-3 dark:bg-n-solid-3';
      }
      const rate = cell.read_rate;
      if (rate >= 75) return 'bg-indigo-600 dark:bg-indigo-500';
      if (rate >= 50) return 'bg-indigo-400 dark:bg-indigo-600';
      if (rate >= 25) return 'bg-indigo-300 dark:bg-indigo-700';
      return 'bg-indigo-200 dark:bg-indigo-800';
    },

    heatmapCellTooltip(cell, dayLabel) {
      if (cell.sent === 0)
        return `${dayLabel} ${String(cell.hour).padStart(2, '0')}h · sem dados`;
      return `${dayLabel} ${String(cell.hour).padStart(2, '0')}h · ${cell.sent} enviadas · ${cell.read} lidas (${fmtPct(cell.read_rate)})`;
    },

    async fetchReport() {
      this.loading = true;
      this.error = null;
      try {
        const { from, to } = this.dateRange;
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/inboxes/${this.inboxId}/whatsapp_campaign_metrics/report`,
          { params: { from, to } }
        );
        this.report = data;
      } catch (err) {
        this.error = this.mapFetchError(err);
      } finally {
        this.loading = false;
      }
    },

    setPeriod(p) {
      this.period = p;
    },

    mapFetchError(err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        return this.$t('INBOX_MGMT.WHATSAPP_REPORTS.ACCESS_ERROR');
      }
      if (status >= 500) {
        return this.$t('INBOX_MGMT.WHATSAPP_REPORTS.SERVER_ERROR');
      }
      if (status) {
        return this.$t('INBOX_MGMT.WHATSAPP_REPORTS.LOAD_ERROR');
      }
      return this.$t('INBOX_MGMT.WHATSAPP_REPORTS.CONNECTION_ERROR');
    },
  },
};
</script>

<template>
  <div class="p-5 text-n-slate-12 text-sm flex flex-col gap-4">
    <!-- Quality alert -->
    <div
      v-if="alertLevel === 'red' && !loading"
      class="flex items-center gap-2 p-3 rounded-2xl bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-900/40"
    >
      <span class="i-ri-alert-line size-4 flex-shrink-0" />
      <span class="text-xs font-semibold">
        Alto volume de erros — taxa de falhas: {{ fmtPct(totals.failure_rate) }}
      </span>
    </div>
    <div
      v-else-if="alertLevel === 'amber' && !loading"
      class="flex items-center gap-2 p-3 rounded-2xl bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900/40"
    >
      <span class="i-ri-error-warning-line size-4 flex-shrink-0" />
      <span class="text-xs font-semibold">
        Qualidade do número pode estar em risco — taxa de falhas:
        {{ fmtPct(totals.failure_rate) }}
      </span>
    </div>

    <!-- Header + period selector -->
    <div class="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h2 class="text-base font-bold text-n-slate-12 m-0">
          Relatórios WhatsApp
        </h2>
        <p class="text-xs text-n-slate-11 mt-0.5">
          Métricas agregadas de envio, entrega, leitura, custo e receita
        </p>
        <button
          class="mt-1 text-xs text-n-blue-text underline"
          @click="showRevenueSettings = !showRevenueSettings"
        >
          {{
            showRevenueSettings
              ? 'Fechar rastreamento de vendas'
              : 'Configurar rastreamento de vendas'
          }}
        </button>
      </div>

      <div class="flex items-center gap-1.5 flex-wrap">
        <button
          v-for="p in [
            { id: '7d', label: '7 dias' },
            { id: '30d', label: '30 dias' },
            { id: 'custom', label: 'Personalizado' },
          ]"
          :key="p.id"
          class="px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors"
          :class="
            period === p.id
              ? 'bg-n-blue-9 text-white border-n-blue-9'
              : 'bg-n-surface-1 text-n-slate-11 border-n-weak hover:bg-n-slate-3 dark:hover:bg-n-solid-3'
          "
          @click="setPeriod(p.id)"
        >
          {{ p.label }}
        </button>

        <template v-if="period === 'custom'">
          <input
            v-model="customFrom"
            type="date"
            class="px-2 py-1.5 rounded-xl text-xs border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-blue-9"
            :max="customTo"
          />
          <span class="text-xs text-n-slate-11">até</span>
          <input
            v-model="customTo"
            type="date"
            class="px-2 py-1.5 rounded-xl text-xs border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:border-n-blue-9"
            :min="customFrom"
          />
        </template>
      </div>
    </div>

    <RevenueTrackingSettings v-if="showRevenueSettings" />

    <!-- Loading -->
    <div
      v-if="loading"
      class="flex flex-col items-center justify-center gap-3 py-12 text-n-slate-11"
    >
      <span class="i-ri-loader-4-line size-6 animate-spin" />
      <span>Carregando relatório...</span>
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      class="flex flex-col items-center justify-center gap-3 py-12 text-n-slate-11"
    >
      <span class="i-ri-wifi-off-line size-6" />
      <span>{{ error }}</span>
      <button
        class="px-3 py-1.5 rounded-xl text-xs font-medium bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 dark:bg-n-solid-3 dark:hover:bg-n-solid-2"
        @click="fetchReport"
      >
        Tentar novamente
      </button>
    </div>

    <!-- Content -->
    <template v-else-if="report">
      <!-- KPI Cards -->
      <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <!-- Campaigns count -->
        <div
          class="rounded-2xl border border-n-weak bg-n-surface-1 p-4 flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-megaphone-line size-4 text-indigo-500" />
            <span
              class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
              >Campanhas</span
            >
          </div>
          <span class="text-2xl font-extrabold text-n-slate-12">{{
            fmtNum(totals.campaigns_count)
          }}</span>
          <span class="text-[0.6875rem] text-n-slate-11">no período</span>
        </div>

        <!-- Sent -->
        <div
          class="rounded-2xl border border-n-weak bg-n-surface-1 p-4 flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-send-plane-line size-4 text-blue-500" />
            <span
              class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
              >Enviadas</span
            >
          </div>
          <span class="text-2xl font-extrabold text-n-slate-12">{{
            fmtNum(totals.total_sent)
          }}</span>
          <span class="text-[0.6875rem]" :class="deltaClass(sentDelta, true)">
            {{ sentDelta.label }} vs anterior
          </span>
        </div>

        <!-- Delivery rate -->
        <div
          class="rounded-2xl border border-n-weak bg-n-surface-1 p-4 flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-checkbox-circle-line size-4 text-teal-500" />
            <span
              class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
              >Entregues</span
            >
          </div>
          <span class="text-2xl font-extrabold text-n-slate-12">{{
            fmtPct(totals.delivery_rate)
          }}</span>
          <span
            class="text-[0.6875rem]"
            :class="deltaClass(deliveryDelta, true)"
          >
            {{ deliveryDelta.label }} vs anterior
          </span>
        </div>

        <!-- Read rate -->
        <div
          class="rounded-2xl border border-n-weak bg-n-surface-1 p-4 flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-eye-line size-4 text-amber-500" />
            <span
              class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
              >Lidas</span
            >
          </div>
          <span class="text-2xl font-extrabold text-n-slate-12">{{
            fmtPct(totals.read_rate)
          }}</span>
          <span class="text-[0.6875rem]" :class="deltaClass(readDelta, true)">
            {{ readDelta.label }} vs anterior
          </span>
        </div>

        <!-- Reply rate -->
        <div
          class="rounded-2xl border border-n-weak bg-n-surface-1 p-4 flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <span
              class="i-ri-chat-follow-up-line size-4 text-purple-500"
            />
            <span
              class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
              >Respostas</span
            >
          </div>
          <span class="text-2xl font-extrabold text-n-slate-12">{{
            fmtPct(totals.reply_rate)
          }}</span>
          <span class="text-[0.6875rem]" :class="deltaClass(replyDelta, true)">
            {{ replyDelta.label }} vs anterior
          </span>
        </div>

        <!-- Cost -->
        <div
          class="rounded-2xl border border-n-weak bg-n-surface-1 p-4 flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-cash-line size-4 text-green-500" />
            <span
              class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
              >{{ $t('INBOX_MGMT.WHATSAPP_REPORTS.ESTIMATED_COST') }}</span
            >
          </div>
          <span class="text-xl font-extrabold text-n-slate-12">
            {{ fmtMoney(cost.total, cost.currency) }}
          </span>
          <span class="text-[0.6875rem] text-n-slate-11">
            {{
              fmtNum(
                cost.billable_counts.marketing +
                  cost.billable_counts.utility +
                  cost.billable_counts.authentication +
                  cost.billable_counts.service
              )
            }}
            cobráveis
          </span>
        </div>
      </div>

      <!-- Revenue (only for accounts tracking sales) -->
      <div
        v-if="hasRevenue"
        class="rounded-2xl border border-n-weak bg-n-surface-1 p-4 flex flex-col gap-3"
      >
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <h3 class="text-sm font-semibold text-n-slate-12 m-0">
            Retorno das campanhas
          </h3>
          <span class="text-[0.6875rem] text-n-slate-11">
            vendas atribuídas por UTM ou pelo contato do comprador
          </span>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <span class="i-ri-arrow-right-up-long-line size-4 text-emerald-500" />
              <span
                class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
                >Receita</span
              >
            </div>
            <span class="text-2xl font-extrabold text-n-slate-12">
              {{ fmtMoney(revenue.total, revenue.currency) }}
            </span>
            <span class="text-[0.6875rem] text-n-slate-11">
              custo de envio {{ fmtMoney(cost.total, cost.currency) }}
            </span>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <span class="i-ri-shopping-bag-line size-4 text-indigo-500" />
              <span
                class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
                >Vendas</span
              >
            </div>
            <span class="text-2xl font-extrabold text-n-slate-12">
              {{ fmtNum(revenue.conversions_count) }}
            </span>
            <span class="text-[0.6875rem] text-n-slate-11">no período</span>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <span class="i-ri-receipt-line size-4 text-sky-500" />
              <span
                class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
                >Ticket médio</span
              >
            </div>
            <span class="text-2xl font-extrabold text-n-slate-12">
              {{ fmtMoney(revenue.avg_ticket, revenue.currency) }}
            </span>
            <span class="text-[0.6875rem] text-n-slate-11">
              {{ fmtMoney(revenue.revenue_per_reply, revenue.currency) }} por
              resposta
            </span>
          </div>

          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <span class="i-ri-percent-line size-4 text-amber-500" />
              <span
                class="text-[0.6875rem] text-n-slate-11 uppercase tracking-wide"
                >ROI</span
              >
            </div>
            <span class="text-2xl font-extrabold" :class="roiClass">
              {{ roiLabel }}
            </span>
            <span class="text-[0.6875rem] text-n-slate-11">
              sobre o custo de envio
            </span>
          </div>
        </div>
      </div>

      <!-- Line chart -->
      <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-4">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
          Atividade diária
        </h3>
        <div
          v-if="report.daily_stats && report.daily_stats.length"
          class="h-64"
        >
          <Line :data="chartData" :options="chartOptions" />
        </div>
        <div v-else class="text-center py-8 text-n-slate-11">
          Sem dados no período selecionado.
        </div>
      </div>

      <!-- Two-column: top campaigns + cost breakdown -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- Top campaigns -->
        <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-4">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
            Top campanhas por volume
          </h3>
          <div v-if="topCampaigns.length" class="flex flex-col gap-2">
            <div
              v-for="(tc, idx) in topCampaigns"
              :key="tc.id"
              class="flex items-center gap-3 p-2 rounded-xl hover:bg-n-slate-2 dark:hover:bg-n-solid-2"
            >
              <span
                class="size-6 rounded-full bg-n-slate-3 dark:bg-n-solid-3 flex items-center justify-center text-[0.6875rem] font-bold text-n-slate-11"
              >
                {{ idx + 1 }}
              </span>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-semibold text-n-slate-12 truncate">
                  {{ tc.title }}
                </div>
                <div class="text-[0.6875rem] text-n-slate-11">
                  {{ fmtNum(tc.total_sent) }} enviadas ·
                  {{ fmtPct(tc.read_rate) }} lidas ·
                  {{ fmtPct(tc.reply_rate) }} reply
                  <template v-if="tc.revenue_total > 0">
                    ·
                    <span class="text-emerald-600 dark:text-emerald-400">
                      {{ fmtMoney(tc.revenue_total, revenue.currency) }}
                    </span>
                  </template>
                </div>
              </div>
              <span
                class="px-2 py-0.5 rounded-full text-[0.6875rem] font-semibold flex-shrink-0"
                :class="
                  tc.read_rate >= 30
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : tc.read_rate >= 10
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      : 'bg-n-slate-3 text-n-slate-11 dark:bg-n-solid-3'
                "
              >
                {{ fmtPct(tc.read_rate) }}
              </span>
            </div>
          </div>
          <div v-else class="text-center py-8 text-n-slate-11">
            Nenhuma campanha no período.
          </div>
        </div>

        <!-- Cost breakdown -->
        <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-4">
          <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
            Custo por categoria
          </h3>
          <div class="flex flex-col gap-2">
            <div
              v-for="(amount, category) in cost.by_category"
              :key="category"
              class="flex items-center justify-between p-2 rounded-xl bg-n-slate-2 dark:bg-n-solid-2"
            >
              <div class="flex items-center gap-2">
                <span
                  :class="categoryIcon(category)"
                  class="size-4 text-n-slate-11"
                />
                <span class="text-sm text-n-slate-12">{{
                  categoryLabel(category)
                }}</span>
                <span class="text-[0.6875rem] text-n-slate-11">
                  ({{ fmtNum(cost.billable_counts[category]) }} msgs)
                </span>
              </div>
              <span class="text-sm font-bold text-n-slate-12">
                {{ fmtMoney(amount, cost.currency) }}
              </span>
            </div>
          </div>
          <div
            class="mt-3 pt-3 border-t border-n-weak flex items-center justify-between"
          >
            <span class="text-sm font-semibold text-n-slate-12">Total</span>
            <span class="text-lg font-extrabold text-n-slate-12">
              {{ fmtMoney(cost.total, cost.currency) }}
            </span>
          </div>
          <div
            class="text-[0.6875rem] text-n-slate-11 flex items-center gap-1 pt-2"
          >
            <span class="i-ri-information-line size-3" />
            Estimativa baseada em tarifas padrão BR. Edite em account.settings.
          </div>
        </div>
      </div>

      <!-- Hourly heatmap -->
      <div class="rounded-2xl border border-n-weak bg-n-surface-1 p-4">
        <div class="flex items-start justify-between gap-2 flex-wrap mb-3">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              Melhor horário para envio
            </h3>
            <p class="text-[0.6875rem] text-n-slate-11 mt-0.5">
              Cor indica a taxa de leitura (%) por dia da semana × hora
            </p>
          </div>
          <div
            class="flex items-center gap-1.5 text-[0.6875rem] text-n-slate-11"
          >
            <span>baixa</span>
            <span
              class="inline-block size-3 rounded-sm bg-indigo-200 dark:bg-indigo-800"
            />
            <span
              class="inline-block size-3 rounded-sm bg-indigo-300 dark:bg-indigo-700"
            />
            <span
              class="inline-block size-3 rounded-sm bg-indigo-400 dark:bg-indigo-600"
            />
            <span
              class="inline-block size-3 rounded-sm bg-indigo-600 dark:bg-indigo-500"
            />
            <span>alta</span>
          </div>
        </div>

        <div v-if="heatmapHasData" class="overflow-x-auto">
          <div class="inline-block min-w-full">
            <!-- Hours header -->
            <div class="flex items-center gap-[1px] mb-1 pl-10">
              <div
                v-for="h in 24"
                :key="h - 1"
                class="w-[18px] text-center text-[9px] text-n-slate-11 flex-shrink-0"
              >
                {{ String(h - 1).padStart(2, '0') }}
              </div>
            </div>

            <!-- Rows -->
            <div
              v-for="row in heatmapRows"
              :key="row.label"
              class="flex items-center gap-[1px] mb-[1px]"
            >
              <div
                class="w-10 text-[10px] text-n-slate-11 flex-shrink-0 text-right pr-2"
              >
                {{ row.label }}
              </div>
              <div
                v-for="cell in row.cells"
                :key="`${row.label}-${cell.hour}`"
                :class="heatmapCellClass(cell)"
                :title="heatmapCellTooltip(cell, row.label)"
                class="w-[18px] h-[18px] rounded-[3px] flex-shrink-0 transition-opacity hover:opacity-80 cursor-help"
              />
            </div>
          </div>
        </div>

        <div v-else class="text-center py-8 text-n-slate-11 text-xs">
          Sem envios suficientes no período para gerar heatmap.
        </div>
      </div>
    </template>

    <!-- Empty state (no report yet) -->
    <div
      v-else
      class="flex flex-col items-center justify-center gap-3 py-12 text-n-slate-11"
    >
      <span class="i-ri-bar-chart-2-line size-8" />
      <p>Nenhum dado disponível.</p>
    </div>
  </div>
</template>
